import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import { bus } from '../App';

type TagRecord = { id: number; name: string };
type TagSummary = { id: number; name: string; usageCount: number };

interface TagEditorProps {
  entityType: string;
  entityId: number;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
}

type PendingAction = 'assign' | 'remove';

const TAG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const TOP_TAG_LIMIT = 12;

export function TagEditor({ entityType, entityId, onClose, anchorEl }: TagEditorProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [allTags, setAllTags] = useState<TagSummary[]>([]);
  const [entityTags, setEntityTags] = useState<TagRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ name: string; action: PendingAction } | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const normalizedQuery = query.trim().toLowerCase();

  const fetchTagData = useCallback(async () => {
    const [summary, assigned] = await Promise.all([
      api().services.tags.listTagSummary(),
      api().services.tags.getTagsForEntity(entityType, entityId),
    ]);
    return {
      summary: Array.isArray(summary) ? summary : [],
      assigned: Array.isArray(assigned) ? assigned : [],
    };
  }, [entityType, entityId]);

  const refreshAll = useCallback(async () => {
    try {
      const { summary, assigned } = await fetchTagData();
      setAllTags(summary);
      setEntityTags(assigned);
    } catch (err) {
      console.error('[TagEditor] Failed to refresh tags:', err);
    }
  }, [fetchTagData]);

  const refreshSummaryOnly = useCallback(async () => {
    try {
      const summary = await api().services.tags.listTagSummary();
      setAllTags(Array.isArray(summary) ? summary : []);
    } catch (err) {
      console.error('[TagEditor] Failed to refresh tag summary:', err);
    }
  }, []);

  // Position menu intelligently - prefer above, avoid edges
  useEffect(() => {
    if (!anchorEl || !menuRef.current) return;

    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      const popupRect = menuRef.current?.getBoundingClientRect();

      if (!popupRect) return;

      const SPACING = 4;
      const EDGE_BUFFER = 8;

      let top = 0;
      let left = 0;
      let currentPlacement = 'top-left';

      // Try to position above
      const topSpace = rect.top;
      const bottomSpace = window.innerHeight - rect.bottom;
      const preferTop = topSpace > bottomSpace || topSpace > popupRect.height + SPACING;

      // Vertical positioning
      if (preferTop) {
        top = rect.top - popupRect.height - SPACING;
        currentPlacement = currentPlacement.replace('top', 'top');
      } else {
        top = rect.bottom + SPACING;
        currentPlacement = currentPlacement.replace('top', 'bottom');
      }

      // Horizontal positioning - prefer left aligned but avoid right edge
      const rightSpace = window.innerWidth - rect.left;
      const leftSpace = rect.right;

      if (rightSpace >= popupRect.width + EDGE_BUFFER) {
        // Can fit on right side of anchor
        left = rect.left;
        currentPlacement = currentPlacement.replace('-left', '-left').replace('-right', '-left');
      } else if (leftSpace >= popupRect.width + EDGE_BUFFER) {
        // Can fit on left side of anchor
        left = rect.right - popupRect.width;
        currentPlacement = currentPlacement.replace('-left', '-right').replace('-right', '-right');
      } else {
        // Center with buffer
        left = Math.max(EDGE_BUFFER, window.innerWidth / 2 - popupRect.width / 2);
      }

      // Clamp top to avoid going off screen
      if (top < EDGE_BUFFER) {
        top = Math.max(EDGE_BUFFER, rect.bottom + SPACING);
      }

      setPosition({ top, left });
    };

    // Small delay to let popup render before measuring
    const timer = requestAnimationFrame(() => {
      updatePosition();
    });

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      cancelAnimationFrame(timer);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorEl]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const { summary, assigned } = await fetchTagData();
        if (!active) {
          return;
        }
        setAllTags(summary);
        setEntityTags(assigned);
      } catch (err) {
        if (!active) {
          return;
        }
        console.error('[TagEditor] Failed to load tags:', err);
        setError('Unable to load tags. Please try again.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [fetchTagData]);

  useEffect(() => {
    const handleChange = async (_event: string, payload?: { entityType?: string; entityId?: number }) => {
      if (payload && payload.entityType === entityType && payload.entityId === entityId) {
        await refreshAll();
      } else {
        await refreshSummaryOnly();
      }
    };

    const unsubscribeAssign = bus.on('tags/assign', handleChange);
    const unsubscribeRemove = bus.on('tags/remove', handleChange);
    const unsubscribeReset = bus.on('tags/reset', async () => {
      await refreshAll();
    });
    const unsubscribeCreate = bus.on('tags/create', async () => {
      await refreshSummaryOnly();
    });
    const unsubscribeDelete = bus.on('tags/delete', async () => {
      await refreshSummaryOnly();
    });

    return () => {
      unsubscribeAssign();
      unsubscribeRemove();
      unsubscribeReset();
      unsubscribeCreate();
      unsubscribeDelete();
    };
  }, [entityType, entityId, refreshAll, refreshSummaryOnly]);

  // Close on outside click or Escape
  useEffect(() => {
    if (!anchorEl) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, anchorEl]);

  const assignedNames = useMemo(() => {
    return new Set(entityTags.map((tag) => tag.name));
  }, [entityTags]);

  const filteredSuggestions = useMemo(() => {
    const base = normalizedQuery
      ? allTags.filter((tag) => tag.name.includes(normalizedQuery))
      : allTags;
    // Filter out already assigned tags
    const unassigned = base.filter((tag) => !assignedNames.has(tag.name));
    return unassigned.slice(0, TOP_TAG_LIMIT);
  }, [allTags, normalizedQuery, assignedNames]);

  const tagExists = useMemo(() => {
    return allTags.some((tag) => tag.name === normalizedQuery);
  }, [allTags, normalizedQuery]);

  const canAdd = Boolean(
    normalizedQuery &&
    TAG_PATTERN.test(normalizedQuery) &&
    !tagExists,
  );

  const isPatternInvalid = Boolean(normalizedQuery && !TAG_PATTERN.test(normalizedQuery));

  const handleAssign = useCallback(async (tagName: string) => {
    if (!tagName || assignedNames.has(tagName)) {
      return;
    }
    setError(null);
    setPending({ name: tagName, action: 'assign' });
    try {
      await api().services.tags.assignTag(tagName, entityType, entityId);
      await refreshAll();
    } catch (err) {
      console.error('[TagEditor] Failed to assign tag:', err);
      const message = err instanceof Error ? err.message : 'Unable to assign tag.';
      setError(message);
    } finally {
      setPending(null);
    }
  }, [assignedNames, entityId, entityType, refreshAll]);

  const handleAdd = useCallback(async () => {
    if (!canAdd || !normalizedQuery) {
      return;
    }
    setError(null);
    setPending({ name: normalizedQuery, action: 'assign' });
    try {
      await api().services.tags.assignTag(normalizedQuery, entityType, entityId);
      setQuery('');
      await refreshAll();
    } catch (err) {
      console.error('[TagEditor] Failed to add tag:', err);
      const message = err instanceof Error ? err.message : 'Unable to add tag.';
      setError(message);
    } finally {
      setPending(null);
    }
  }, [canAdd, entityId, entityType, normalizedQuery, refreshAll]);

  const handleRemove = useCallback(async (tagName: string) => {
    if (!tagName) {
      return;
    }
    setError(null);
    setPending({ name: tagName, action: 'remove' });
    try {
      await api().services.tags.removeTag(tagName, entityType, entityId);
      await refreshAll();
    } catch (err) {
      console.error('[TagEditor] Failed to remove tag:', err);
      const message = err instanceof Error ? err.message : 'Unable to remove tag.';
      setError(message);
    } finally {
      setPending(null);
    }
  }, [entityId, entityType, refreshAll]);

  if (!anchorEl) {
    return null;
  }

  const content = (
    <div
      ref={menuRef}
      className="tag-editor"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className="tag-editor-content">
        {/* Current tags */}
        {entityTags.length > 0 && (
          <div className="tag-editor-tags">
            {entityTags.map((tag) => {
              const busy = pending?.name === tag.name && pending?.action === 'remove';
              return (
                <button
                  key={tag.id}
                  type="button"
                  className="tag-badge tag-badge--assigned"
                  onClick={() => handleRemove(tag.name)}
                  disabled={busy}
                  aria-label={`Remove tag ${tag.name}`}
                  title="Click to remove"
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Input */}
        <input
          type="text"
          className="tag-editor-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && normalizedQuery && TAG_PATTERN.test(normalizedQuery)) {
              event.preventDefault();
              if (canAdd) {
                handleAdd();
              } else if (tagExists) {
                handleAssign(normalizedQuery);
                setQuery('');
              }
            }
          }}
          placeholder="Add or find tags…"
          autoFocus
        />

        {/* Error */}
        {error && <div className="tag-editor-error">{error}</div>}

        {/* Add button */}
        {canAdd && (
          <button
            type="button"
            className="tag-editor-add"
            onClick={handleAdd}
            disabled={pending?.name === normalizedQuery && pending?.action === 'assign'}
          >
            + Add {normalizedQuery}
          </button>
        )}

        {/* Suggestions */}
        {filteredSuggestions.length > 0 && (
          <ul className="tag-editor-suggestions">
            {filteredSuggestions.map((tag) => {
              const isBusy = pending?.name === tag.name && pending?.action === 'assign';
              return (
                <li key={tag.id}>
                  <button
                    type="button"
                    className="tag-badge tag-badge--interactive"
                    onClick={() => handleAssign(tag.name)}
                    disabled={isBusy}
                    aria-label={`Assign tag ${tag.name}`}
                  >
                    {tag.name}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
